import { useState } from "react";
import {
  UploadSimple, Plus, Trash, ArrowUp, ArrowDown, Sliders, Palette, Info,
  Gear, Rows as RowsIcon, Laptop, AppleLogo, WifiHigh, HardDrives, Clock, Key,
  DownloadSimple, ShoppingCartSimple, Lifebuoy, ChatCircle, SquaresFour, AppWindow,
  LinkSimple, Cursor, Compass, UsersThree, Buildings, Circle,
} from "@phosphor-icons/react";

const INFO_TYPES = ["ComputerName", "MacOSVersion", "Network", "Storage", "Uptime", "Password", "AppCatalog"];
const BUTTON_TYPES = ["Button", "ButtonMedium", "ButtonSmall"];
const ACTION_TYPES = ["URL", "App", "Command", "PrivilegedScript"];
const PASSWORD_TYPES = ["Apple", "JamfConnect", "KerberosSSO", "Nomad"];

const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

const SF_ICON = {
  "macwindow.and.pointer.arrow": Cursor, "cursorarrow.click.2": Cursor,
  "lifepreserver": Lifebuoy, "cart.badge.plus": ShoppingCartSimple, "cart": ShoppingCartSimple,
  "key.fill": Key, "key": Key, "bubble.left.fill": ChatCircle, "message": ChatCircle,
  "square.grid.3x2": SquaresFour, "square.grid.2x2": SquaresFour, "app.badge": AppWindow,
  "safari": Compass, "network": WifiHigh, "person.2": UsersThree, "person.2.badge.gearshape": UsersThree,
  "building.2": Buildings,
};
const btnIcon = (sym) => SF_ICON[sym] || LinkSimple;

const INFO_META = {
  ComputerName: { icon: Laptop, label: "Computer Name", value: "John's MacBook Pro" },
  MacOSVersion: { icon: AppleLogo, label: "macOS Tahoe", value: "26.1", badge: true },
  Network: { icon: WifiHigh, label: "Wi-Fi", value: "192.168.1.180" },
  Uptime: { icon: Clock, label: "Last Reboot", value: "8 days ago" },
  Password: { icon: Key, label: "Mac Password", value: "Expires in 361 days" },
  AppCatalog: { icon: DownloadSimple, label: "Apps", value: "Updates Available", badge: true },
  Storage: { icon: HardDrives, label: "Storage", value: "71.7% Used", storage: true },
};

// small toggle
const Toggle = ({ v, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${v ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
    <span className={`w-8 h-4 rounded-full relative transition-colors duration-200 ${v ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
      <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${v ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
    </span>
    {v ? "Enabled" : "Disabled"}
  </button>
);

const Field = ({ label, children }) => (
  <div><label className={labelCls}>{label}</label>{children}</div>
);

const TABS = [
  { id: "layout", label: "Layout", icon: RowsIcon },
  { id: "general", label: "General", icon: Sliders },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "info", label: "Info items", icon: Info },
  { id: "advanced", label: "Advanced", icon: Gear },
];

export function SupportAppEditor({ values, setVal, handleUpload }) {
  const [tab, setTab] = useState("layout");
  const rows = Array.isArray(values.rows) ? values.rows : [];

  const setRows = (next) => setVal("rows", next);
  const addRow = () => setRows([...rows, { items: [{ Type: "ButtonMedium", Title: "New Button", Subtitle: "", Symbol: "link", ActionType: "URL", Action: "https://" }] }]);
  const removeRow = (ri) => setRows(rows.filter((_, i) => i !== ri));
  const moveRow = (ri, d) => {
    const to = ri + d; if (to < 0 || to >= rows.length) return;
    const next = [...rows]; const [m] = next.splice(ri, 1); next.splice(to, 0, m); setRows(next);
  };
  const setRowItems = (ri, items) => setRows(rows.map((r, i) => (i === ri ? { ...r, items } : r)));
  const addItem = (ri) => {
    const items = rows[ri].items || [];
    setRowItems(ri, [...items, { Type: "ComputerName", Title: "", Subtitle: "", Symbol: "", ActionType: "URL", Action: "" }]);
  };
  const updateItem = (ri, ii, key, val) => setRowItems(ri, (rows[ri].items || []).map((it, i) => (i === ii ? { ...it, [key]: val } : it)));
  const removeItem = (ri, ii) => setRowItems(ri, (rows[ri].items || []).filter((_, i) => i !== ii));
  const moveItem = (ri, ii, d) => {
    const items = [...(rows[ri].items || [])]; const to = ii + d; if (to < 0 || to >= items.length) return;
    const [m] = items.splice(ii, 1); items.splice(to, 0, m); setRowItems(ri, items);
  };

  const uploadField = (key, label) => (
    <Field label={label}>
      <div className="flex gap-2">
        <input data-testid={`sa-${key}`} value={values[key] ?? ""} onChange={(e) => setVal(key, e.target.value)} className={inputCls} placeholder="URL, path or SF Symbol" />
        <label className="flex items-center gap-1.5 border border-[#27272A] px-3 text-xs text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] cursor-pointer transition-colors duration-200 whitespace-nowrap">
          <UploadSimple size={14} /> Upload
          <input type="file" accept="image/*" className="hidden" data-testid={`sa-upload-${key}`} onChange={(e) => handleUpload(key, e)} />
        </label>
      </div>
    </Field>
  );

  return (
    <div data-testid="sa-editor">
      <div className="flex flex-wrap gap-1 mb-5 border-b border-[#27272A]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`sa-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title"><input data-testid="sa-Title" value={values.Title ?? ""} onChange={(e) => setVal("Title", e.target.value)} className={inputCls} /></Field>
          <Field label="Footer text (markdown)"><input data-testid="sa-FooterText" value={values.FooterText ?? ""} onChange={(e) => setVal("FooterText", e.target.value)} className={inputCls} /></Field>
          <Field label="Error message"><input data-testid="sa-ErrorMessage" value={values.ErrorMessage ?? ""} onChange={(e) => setVal("ErrorMessage", e.target.value)} className={inputCls} /></Field>
          <Field label="Show welcome screen"><Toggle testid="sa-ShowWelcomeScreen" v={!!values.ShowWelcomeScreen} onClick={() => setVal("ShowWelcomeScreen", !values.ShowWelcomeScreen)} /></Field>
          <Field label="Menu-bar notification badge"><Toggle testid="sa-StatusBarIconNotifierEnabled" v={!!values.StatusBarIconNotifierEnabled} onClick={() => setVal("StatusBarIconNotifierEnabled", !values.StatusBarIconNotifierEnabled)} /></Field>
        </div>
      )}

      {tab === "branding" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {uploadField("Logo", "Logo (light)")}
          {uploadField("LogoDarkMode", "Logo (dark)")}
          {uploadField("NotificationIcon", "Notification icon")}
          {uploadField("StatusBarIcon", "Menu-bar icon image")}
          <Field label="Menu-bar SF Symbol"><input data-testid="sa-StatusBarIconSFSymbol" value={values.StatusBarIconSFSymbol ?? ""} onChange={(e) => setVal("StatusBarIconSFSymbol", e.target.value)} className={inputCls} /></Field>
          <Field label="Allow color on custom menu-bar icon"><Toggle testid="sa-StatusBarIconAllowsColor" v={!!values.StatusBarIconAllowsColor} onClick={() => setVal("StatusBarIconAllowsColor", !values.StatusBarIconAllowsColor)} /></Field>
          <Field label="Accent color — light">
            <div className="flex gap-2 items-center">
              <input type="color" data-testid="sa-CustomColor" value={values.CustomColor || "#0A84FF"} onChange={(e) => setVal("CustomColor", e.target.value)} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" />
              <input value={values.CustomColor ?? ""} onChange={(e) => setVal("CustomColor", e.target.value)} className={inputCls} />
            </div>
          </Field>
          <Field label="Accent color — dark">
            <div className="flex gap-2 items-center">
              <input type="color" data-testid="sa-CustomColorDarkMode" value={values.CustomColorDarkMode || "#0A84FF"} onChange={(e) => setVal("CustomColorDarkMode", e.target.value)} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" />
              <input value={values.CustomColorDarkMode ?? ""} onChange={(e) => setVal("CustomColorDarkMode", e.target.value)} className={inputCls} />
            </div>
          </Field>
        </div>
      )}

      {tab === "info" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Uptime warning after (days, 0 = off)"><input type="number" data-testid="sa-UptimeDaysLimit" value={values.UptimeDaysLimit ?? 0} onChange={(e) => setVal("UptimeDaysLimit", e.target.value)} className={inputCls} /></Field>
          <Field label="Storage warning at (% used, 0 = off)"><input type="number" data-testid="sa-StorageLimit" value={values.StorageLimit ?? 0} onChange={(e) => setVal("StorageLimit", e.target.value)} className={inputCls} /></Field>
          <Field label="Password source">
            <select data-testid="sa-PasswordType" value={values.PasswordType ?? "Apple"} onChange={(e) => setVal("PasswordType", e.target.value)} className={inputCls}>
              {PASSWORD_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Password expiry warning (days, 0 = off)"><input type="number" data-testid="sa-PasswordExpiryLimit" value={values.PasswordExpiryLimit ?? 0} onChange={(e) => setVal("PasswordExpiryLimit", e.target.value)} className={inputCls} /></Field>
          <Field label="Password item label"><input data-testid="sa-PasswordLabel" value={values.PasswordLabel ?? ""} onChange={(e) => setVal("PasswordLabel", e.target.value)} className={inputCls} /></Field>
          <Field label="Software update popover text"><input data-testid="sa-UpdateText" value={values.UpdateText ?? ""} onChange={(e) => setVal("UpdateText", e.target.value)} className={inputCls} /></Field>
        </div>
      )}

      {tab === "advanced" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Open at login (non-PKG build)"><Toggle testid="sa-OpenAtLogin" v={!!values.OpenAtLogin} onClick={() => setVal("OpenAtLogin", !values.OpenAtLogin)} /></Field>
          <Field label="Disable privileged helper tool"><Toggle testid="sa-DisablePrivilegedHelperTool" v={!!values.DisablePrivilegedHelperTool} onClick={() => setVal("DisablePrivilegedHelperTool", !values.DisablePrivilegedHelperTool)} /></Field>
          <Field label="Disable Configurator Mode"><Toggle testid="sa-DisableConfiguratorMode" v={!!values.DisableConfiguratorMode} onClick={() => setVal("DisableConfiguratorMode", !values.DisableConfiguratorMode)} /></Field>
          <Field label="OnAppear privileged script path"><input data-testid="sa-OnAppearAction" value={values.OnAppearAction ?? ""} onChange={(e) => setVal("OnAppearAction", e.target.value)} className={inputCls} /></Field>
          <Field label="Signed .pkg URL (blank = latest release)"><input data-testid="sa-pkg_url" value={values.pkg_url ?? ""} onChange={(e) => setVal("pkg_url", e.target.value)} className={inputCls} /></Field>
        </div>
      )}

      {tab === "layout" && (
        <div className="space-y-4" data-testid="sa-rows">
          <p className="font-mono text-[10px] text-[#52525B]">Each row holds one or more items. Info items (Computer Name, Storage, etc.) render as tiles; buttons open apps, URLs, commands or privileged scripts. Medium buttons fit 3 per row, small buttons 4 per row.</p>
          {rows.map((row, ri) => (
            <div key={ri} data-testid={`sa-row-${ri}`} className="border border-[#27272A] bg-[#0f0f11]">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] bg-[#121214]">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#71717A]">Row {ri + 1}</span>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`sa-row-up-${ri}`} disabled={ri === 0} onClick={() => moveRow(ri, -1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowUp size={13} /></button>
                  <button data-testid={`sa-row-down-${ri}`} disabled={ri === rows.length - 1} onClick={() => moveRow(ri, 1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowDown size={13} /></button>
                  <button data-testid={`sa-row-remove-${ri}`} onClick={() => removeRow(ri)} className="text-[#71717A] hover:text-[#FF5F56] ml-1"><Trash size={14} /></button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {(row.items || []).map((it, ii) => {
                  const isBtn = BUTTON_TYPES.includes(it.Type);
                  return (
                    <div key={ii} data-testid={`sa-item-${ri}-${ii}`} className="border border-[#27272A] bg-[#050505] p-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <select data-testid={`sa-item-type-${ri}-${ii}`} value={it.Type} onChange={(e) => updateItem(ri, ii, "Type", e.target.value)} className={inputCls + " flex-1"}>
                          <optgroup label="Info items">{INFO_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}</optgroup>
                          <optgroup label="Buttons">{BUTTON_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}</optgroup>
                        </select>
                        <button data-testid={`sa-item-up-${ri}-${ii}`} disabled={ii === 0} onClick={() => moveItem(ri, ii, -1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowUp size={13} /></button>
                        <button data-testid={`sa-item-down-${ri}-${ii}`} disabled={ii === (row.items.length - 1)} onClick={() => moveItem(ri, ii, 1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowDown size={13} /></button>
                        <button data-testid={`sa-item-remove-${ri}-${ii}`} onClick={() => removeItem(ri, ii)} className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={13} /></button>
                      </div>
                      {isBtn && (
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid={`sa-item-title-${ri}-${ii}`} placeholder="Title" value={it.Title ?? ""} onChange={(e) => updateItem(ri, ii, "Title", e.target.value)} className={inputCls} />
                          <input data-testid={`sa-item-subtitle-${ri}-${ii}`} placeholder="Subtitle (hover)" value={it.Subtitle ?? ""} onChange={(e) => updateItem(ri, ii, "Subtitle", e.target.value)} className={inputCls} />
                          <input data-testid={`sa-item-symbol-${ri}-${ii}`} placeholder="SF Symbol" value={it.Symbol ?? ""} onChange={(e) => updateItem(ri, ii, "Symbol", e.target.value)} className={inputCls} />
                          <select data-testid={`sa-item-actiontype-${ri}-${ii}`} value={it.ActionType ?? "URL"} onChange={(e) => updateItem(ri, ii, "ActionType", e.target.value)} className={inputCls}>
                            {ACTION_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <input data-testid={`sa-item-action-${ri}-${ii}`} placeholder="URL / bundle id / command / script path" value={it.Action ?? ""} onChange={(e) => updateItem(ri, ii, "Action", e.target.value)} className={inputCls + " col-span-2"} />
                        </div>
                      )}
                    </div>
                  );
                })}
                <button data-testid={`sa-add-item-${ri}`} onClick={() => addItem(ri)} className="flex items-center gap-1.5 border border-[#27272A] text-[#A1A1AA] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><Plus size={13} /> Add item</button>
              </div>
            </div>
          ))}
          <button data-testid="sa-add-row" onClick={addRow} className="flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] px-4 py-2 text-xs hover:bg-[#00F0FF]/10 transition-colors"><Plus size={14} /> Add row</button>
        </div>
      )}
    </div>
  );
}

// ---- realistic macOS Support App preview ----
export function SupportAppPreview({ values }) {
  const accent = values.CustomColor || "#0A84FF";
  const rows = Array.isArray(values.rows) ? values.rows : [];
  const title = (values.Title || "Support").replace("$LocalFullName", "John Appleseed").replace("$LocalComputerName", "John's MacBook Pro");
  const footer = (values.FooterText || "").replace(/\*\*(.+?)\*\*/g, "$1");

  const IconCircle = ({ Icon, size = 36 }) => (
    <span className="rounded-full bg-white flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <Icon size={size * 0.5} weight="fill" style={{ color: accent }} />
    </span>
  );

  const InfoTile = ({ type }) => {
    const m = INFO_META[type] || { icon: Circle, label: type, value: "" };
    const label = type === "Password" ? (values.PasswordLabel || m.label) : m.label;
    return (
      <div className="flex-1 min-w-0 flex items-center gap-3 rounded-full bg-white/[0.07] border border-white/10 px-3 py-2.5 relative">
        <IconCircle Icon={m.icon} />
        <div className="min-w-0 flex-1">
          {m.storage ? (
            <>
              <div className="text-white text-[13px] font-semibold leading-tight">{m.value}</div>
              <div className="mt-1.5 h-2 rounded-full bg-white/15 overflow-hidden"><div className="h-full rounded-full" style={{ width: "71.7%", background: accent }} /></div>
            </>
          ) : (
            <>
              <div className="text-white text-[13px] font-semibold leading-tight truncate">{label}</div>
              <div className="text-white/60 text-[11px] truncate">{m.value}</div>
            </>
          )}
        </div>
        {m.badge && <span className="absolute -top-1 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-bold flex items-center justify-center">1</span>}
      </div>
    );
  };

  const MediumBtn = ({ it }) => {
    const Icon = btnIcon(it.Symbol);
    return (
      <div className="flex-1 min-w-0 flex flex-col items-center justify-center gap-2 rounded-[20px] bg-white/[0.07] border border-white/10 py-3 px-1" title={it.Subtitle || it.Action}>
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.12)" }}><Icon size={18} weight="fill" className="text-white" /></span>
        <span className="text-white text-[11px] font-medium text-center truncate max-w-full px-1">{it.Title || "Button"}</span>
      </div>
    );
  };
  const SmallBtn = ({ it }) => {
    const Icon = btnIcon(it.Symbol);
    return (
      <div className="flex-1 flex flex-col items-center gap-1.5" title={it.Title}>
        <span className="w-11 h-11 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center"><Icon size={18} weight="fill" className="text-white" /></span>
        <span className="text-white/70 text-[9px] text-center truncate max-w-[52px]">{it.Title}</span>
      </div>
    );
  };
  const WideBtn = ({ it }) => {
    const Icon = btnIcon(it.Symbol);
    return (
      <div className="w-full flex items-center gap-3 rounded-full bg-white/[0.07] border border-white/10 px-3 py-2.5" title={it.Subtitle || it.Action}>
        <IconCircle Icon={Icon} size={32} />
        <span className="text-white text-[13px] font-medium truncate">{it.Title || "Button"}</span>
      </div>
    );
  };

  return (
    <div data-testid="sa-preview">
      <div className="rounded-2xl p-3" style={{ background: `linear-gradient(135deg, ${accent}55, #1b2a3f 70%, #0b1220)` }}>
        <div className="rounded-[24px] p-5 border border-white/10 shadow-2xl" style={{ background: "rgba(14,20,32,0.72)", backdropFilter: "blur(18px)" }}>
          {/* header */}
          <div className="flex items-start justify-between mb-4">
            <div className="text-white text-[22px] font-bold leading-tight pr-2 truncate">{title}</div>
            {values.Logo && (values.Logo.startsWith("http")) ? (
              <img src={values.Logo} alt="logo" className="w-11 h-11 rounded-[12px] object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
            ) : (
              <span className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: accent }}><Lifebuoy size={24} weight="fill" className="text-white" /></span>
            )}
          </div>
          {/* rows */}
          <div className="space-y-2.5">
            {rows.map((row, ri) => {
              const items = row.items || [];
              const small = items.length > 0 && items.every((i) => i.Type === "ButtonSmall");
              return (
                <div key={ri} className={`flex gap-2.5 ${small ? "justify-around" : ""}`}>
                  {items.map((it, ii) => {
                    if (INFO_TYPES.includes(it.Type)) return <InfoTile key={ii} type={it.Type} />;
                    if (it.Type === "ButtonSmall") return <SmallBtn key={ii} it={it} />;
                    if (it.Type === "Button") return <WideBtn key={ii} it={it} />;
                    return <MediumBtn key={ii} it={it} />;
                  })}
                </div>
              );
            })}
          </div>
          {footer && <div className="mt-4 text-white/55 text-[11px]">{footer}</div>}
        </div>
      </div>
      <p className="text-[10px] text-[#71717A] font-mono mt-3 leading-relaxed">
        Live approximation of the macOS Support App (v3.x capsule layout). Info tiles show sample values; badges, real values &amp; SF Symbols render on-device.
      </p>
    </div>
  );
}
